import { createContext, memo, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { pdfScaleSteps } from '@air/react-pdfjs-viewer';
import { findIndexOfLowestClosestNumber } from '@air/react-pdfjs-viewer/src/utils/findIndexOfLowestClosestNumber';
import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PDFZoomContextType = {
  scale: number;
  updateScale: (scale: number) => void;
  updatePDFControls: (controls: { scale: number; updateScale: (scale: number) => void }) => void;
  zoomIn: () => void;
  zoomOut: (zoom?: number) => void;
};

const DEFAULT_VALUE: PDFZoomContextType = {
  scale: 1,
  updateScale: () => {},
  updatePDFControls: () => {},
  zoomIn: () => {},
  zoomOut: () => {},
};

const PDFZoomContext = createContext<PDFZoomContextType>(DEFAULT_VALUE);

export type PDFZoomProviderProps = {
  children: ReactNode;
};

export const PDFZoomProvider = memo(({ children }: PDFZoomProviderProps) => {
  const [scale, setScale] = useState(1);
  const updateScaleRef = useRef<((scale: number) => void) | null>(null);

  const handleUpdateScale = useCallback((newScale: number) => {
    setScale(newScale);
    updateScaleRef.current?.(newScale);
  }, []);

  const updatePDFControls = useCallback((controls: { scale: number; updateScale: (scale: number) => void }) => {
    setScale(controls.scale);
    updateScaleRef.current = controls.updateScale;
  }, []);

  const handleZoomIn = useCallback(() => {
    const currentIndex = findIndexOfLowestClosestNumber(pdfScaleSteps, scale);
    const newIndex = Math.min(pdfScaleSteps.length - 1, currentIndex + 1);
    const newZoom = pdfScaleSteps[newIndex];
    handleUpdateScale(newZoom);
  }, [scale, handleUpdateScale]);

  const handleZoomOut = useCallback(
    (zoom?: number) => {
      if (zoom !== undefined) {
        handleUpdateScale(zoom);
        return;
      }

      const currentIndex = findIndexOfLowestClosestNumber(pdfScaleSteps, scale);
      const newIndex = Math.max(0, currentIndex - 1);
      const newZoom = pdfScaleSteps[newIndex];
      handleUpdateScale(newZoom);
    },
    [scale, handleUpdateScale],
  );

  const contextValue: PDFZoomContextType = useMemo(
    () => ({
      scale,
      updateScale: handleUpdateScale,
      updatePDFControls,
      zoomIn: handleZoomIn,
      zoomOut: handleZoomOut,
    }),
    [scale, handleUpdateScale, updatePDFControls, handleZoomIn, handleZoomOut],
  );

  return <PDFZoomContext.Provider value={contextValue}>{children}</PDFZoomContext.Provider>;
});

PDFZoomProvider.displayName = 'PDFZoomProvider';

export const usePDFZoomContext = (): PDFZoomContextType => {
  const context = useContext(PDFZoomContext);

  if (context === DEFAULT_VALUE) {
    const error = 'usePDFZoomContext must be used within a PDFZoomProvider';

    if (isDevOrTestStage()) {
      throw new Error(error);
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
